import i18next from 'i18next';
import dynamic from 'next/dynamic';
import { ComponentProps, ComponentType } from 'react';

import {
  Attribute,
  AttributeCollection,
  Product,
} from '@/modules/product/api/singleProduct';
import {
  EXCLUDED_ATTRIBUTE_SLUGS,
  EXCLUDED_ATTRIBUTE_VALUES,
} from '@/shared/components/product/productAttributes/constants';
import styles from '@/shared/components/product/productAttributes/ProductAttributes.module.scss';

export const technicalIconsSrc: Record<
  string,
  ComponentType<ComponentProps<'svg'>>
> = {
  technical_icons_1_1: dynamic(() => import('icons/lib/technical_icons_1.svg')),
  technical_icons_2_1: dynamic(() => import('icons/lib/technical_icons_2.svg')),
  technical_icons_3_1: dynamic(() => import('icons/lib/technical_icons_3.svg')),
  technical_icons_4_1: dynamic(() => import('icons/lib/technical_icons_4.svg')),
  technical_icons_5_1: dynamic(() => import('icons/lib/technical_icons_5.svg')),
  technical_icons_6_1: dynamic(() => import('icons/lib/technical_icons_6.svg')),
  technical_icons_7_1: dynamic(() => import('icons/lib/technical_icons_7.svg')),
  technical_icons_8_0: dynamic(
    () => import('icons/lib/technical_icons_8_0.svg')
  ),
  technical_icons_8_1: dynamic(
    () => import('icons/lib/technical_icons_8_1.svg')
  ),
  technical_icons_9_1: dynamic(() => import('icons/lib/technical_icons_9.svg')),
  technical_icons_10_1: dynamic(
    () => import('icons/lib/technical_icons_10.svg')
  ),
  technical_icons_11_1: dynamic(
    () => import('icons/lib/technical_icons_11.svg')
  ),
  technical_icons_12_0: dynamic(
    () => import('icons/lib/technical_icons_12_0.svg')
  ),
  technical_icons_12_1: dynamic(
    () => import('icons/lib/technical_icons_12_1.svg')
  ),
  technical_icons_13_1: dynamic(
    () => import('icons/lib/technical_icons_13.svg')
  ),
};

export function isInductionStoveType(values: string[]) {
  const localesValue = ['indukcyjna', 'indukční', 'indukčný', 'induktion'];

  return localesValue.some((value) => values.includes(value));
}

const SpecificationIconsOrder: Record<string, number> = {
  technical_icons_4: 0,
  technical_icons_8: 1,
  technical_icons_12: 2,
  technical_icons_9: 3,
  technical_icons_10: 4,
  technical_icons_11: 5,
  technical_icons_3: 6,
  technical_icons_2: 7,
  technical_icons_1: 8,
  technical_icons_5: 9,
  technical_icons_7: 10,
  technical_icons_6: 11,
  technical_icons_13: 12,
};

export function sortSpecificationIcons(a: Attribute, b: Attribute) {
  return SpecificationIconsOrder[a.slug] - SpecificationIconsOrder[b.slug];
}

export const sortAttributesByName = (locale: string, languageCode: string) => {
  return (a: Attribute, b: Attribute) =>
    a.name
      ?.toLocaleLowerCase(locale)
      ?.localeCompare(b.name?.toLocaleLowerCase(locale), languageCode);
};

export const sortAttributeIconsByName = (a: Attribute, b: Attribute) => {
  return Number(a.name?.split(' ')[1]) - Number(b.name?.split(' ')[1]);
};

export function isAttributeIcon(attribute: Attribute) {
  return attribute.slug.includes('technical_icons_');
}

export function isEssentialInformation(attribute: Attribute) {
  return attribute?.slug === 'essential_informations';
}

export function isOtherInformationX(attribute: Attribute) {
  return attribute?.slug === 'other_informations_x';
}

export function notEssentialInformation(attribute: Attribute) {
  return !isEssentialInformation(attribute);
}

export function appendUUIDToAttributes(
  attributes: Array<Attribute> | Array<AttributeCollection>,
  uuid: string,
  productType: string
) {
  const productUuidAttribute: Attribute = {
    isRange: false,
    inSpecification: true,
    isFilterable: false,
    name: 'UUID',
    slug: 'UUID',
    unit: '',
    values: [uuid],
  };

  if (productType === 'simple') {
    return (attributes as Array<Attribute>).concat(productUuidAttribute);
  } else {
    return (attributes as Array<AttributeCollection>).map(
      (attributeCollection: AttributeCollection) => ({
        ...attributeCollection,
        attributes: attributeCollection.attributes.concat(productUuidAttribute),
      })
    );
  }
}

export function getTechnicalIcons(product: Product) {
  return product.type === 'simple'
    ? product.attributes.filter(isAttributeIcon).sort(sortAttributeIconsByName)
    : product.attributesCollection.map((attributeCollection) => {
        return attributeCollection.series === 'N/A'
          ? {
              name: attributeCollection.name,
              ean: attributeCollection.ean,
              model: attributeCollection.model,
              manufacturerSymbol: null,
              attributes: attributeCollection.attributes
                .filter(isAttributeIcon)
                .sort(sortAttributeIconsByName),
            }
          : {
              name: attributeCollection.name,
              ean: attributeCollection.ean,
              model: attributeCollection.model,
              series: attributeCollection.series,
              manufacturerSymbol: null,
              attributes: attributeCollection.attributes
                .filter(isAttributeIcon)
                .sort(sortAttributeIconsByName),
            };
      });
}

export function appendUnitToAttributeValues(
  t: typeof i18next.t,
  attribute: Attribute
) {
  return attribute.values
    .map((value) => {
      let unit = '';

      if (attribute?.unit === 'szt.') {
        unit = t('common:common.quantityUnit');
      } else if (attribute?.unit === 'el.') {
        unit = t('common:common.elementQuantityUnit');
      } else if (attribute?.unit !== '') {
        unit = attribute?.unit;
      } else {
        unit = '';
      }

      return `${value} ${unit}`;
    })
    .join(', ');
}

export function getProductAttributes(
  t: typeof i18next.t,
  locale: string,
  languageCode: string,
  product: Product
) {
  const brandName: Attribute[] =
    !product.brandIsHiddenOnProductPage && product.brandName
      ? [
          {
            isRange: false,
            inSpecification: true,
            isFilterable: false,
            name: t('common:common.brand'),
            slug: t('common:common.brand'),
            unit: '',
            values: [product.brandName],
          },
        ]
      : [];

  const series: Attribute[] =
    !product.brandIsHiddenOnProductPage &&
    product.series &&
    product.series !== 'N/A'
      ? [
          {
            isRange: false,
            inSpecification: product.series !== null,
            isFilterable: false,
            name: t('common:common.series'),
            slug: t('common:common.series'),
            unit: '',
            values: [product.series],
          },
        ]
      : [];

  const model: Attribute[] =
    !product.brandIsHiddenOnProductPage &&
    product.model &&
    product.model !== 'N/A'
      ? [
          {
            isRange: false,
            inSpecification: product.model !== null,
            isFilterable: false,
            name: t('common:common.model'),
            slug: t('common:common.model'),
            unit: '',
            values: [product.model],
          },
        ]
      : [];

  return product.type === 'simple'
    ? [
        ...[
          {
            isRange: false,
            inSpecification: true,
            isFilterable: false,
            name: 'EAN',
            slug: 'EAN',
            unit: '',
            values: [product.ean],
          },
          {
            isRange: false,
            inSpecification: product.manufacturerSymbol !== null,
            isFilterable: false,
            name: t('singleProduct.manufacturerCode'),
            slug: t('singleProduct.manufacturerCode'),
            unit: '',
            values: [product.manufacturerSymbol ?? ''],
          },
        ],
        ...brandName,
        ...model,
        ...series,
        ...product.attributes
          .filter((attribute) => {
            return (
              !attribute.values.join().includes('_cs') &&
              !attribute.values.includes('0,00') &&
              !attribute.values.includes('N/A') &&
              !EXCLUDED_ATTRIBUTE_SLUGS.includes(attribute.slug)
            );
          })
          .filter((attribute) =>
            attribute.values.some(
              (value) => !EXCLUDED_ATTRIBUTE_VALUES.includes(value)
            )
          )
          .sort(sortAttributesByName(locale, languageCode)),
      ]
    : product.attributesCollection.map((attributeCollection) => {
        return {
          name: attributeCollection.name,
          ean: attributeCollection.ean,
          model: attributeCollection.model,
          series: attributeCollection.series,
          manufacturerSymbol: null,
          attributes: [
            ...[
              {
                isRange: false,
                inSpecification: true,
                isFilterable: false,
                name: 'EAN',
                slug: 'EAN',
                unit: '',
                values: [attributeCollection.ean],
              },
              {
                isRange: false,
                inSpecification:
                  attributeCollection.manufacturerSymbol !== null,
                isFilterable: false,
                name: t('singleProduct.manufacturerCode'),
                slug: t('singleProduct.manufacturerCode'),
                unit: '',
                values: [attributeCollection.manufacturerSymbol ?? ''],
              },
            ],
            ...brandName,
            ...model,
            ...series,
            ...attributeCollection.attributes
              .filter(
                (attribute) =>
                  !attribute.values.join().includes('_cs') &&
                  !attribute.values.includes('0,00') &&
                  !attribute.values.includes('N/A') &&
                  !EXCLUDED_ATTRIBUTE_SLUGS.includes(attribute.slug)
              )
              .filter((attribute) =>
                attribute.values.some(
                  (value) => !EXCLUDED_ATTRIBUTE_VALUES.includes(value)
                )
              )
              .sort(sortAttributesByName(locale, languageCode)),
          ],
        };
      });
}

export function moveToEndEssentialInformationIndex(
  productAttributes: Array<Attribute | AttributeCollection>
) {
  const essentialInformationIndex = (
    productAttributes as Attribute[]
  ).findIndex(isEssentialInformation);

  if (essentialInformationIndex > -1) {
    const [movedItem] = productAttributes.splice(essentialInformationIndex, 1);
    productAttributes.push(movedItem as Attribute & AttributeCollection);
  }
}

export function moveOtherInformationXIndex(
  productAttributes: Array<Attribute | AttributeCollection>
) {
  const otherInformationXIndex = (productAttributes as Attribute[]).findIndex(
    isOtherInformationX
  );

  if (otherInformationXIndex > -1) {
    const [movedItem] = productAttributes.splice(otherInformationXIndex, 1);
    productAttributes.push(movedItem as Attribute & AttributeCollection);
  }
}
